import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const InventoryTable = ({ 
  inventory = [], 
  selectedItems = [], 
  onSelectionChange, 
  onRevaluate,
  onConfigureAlert,
  onViewDetails 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sort and filter inventory
  const filteredAndSortedInventory = useMemo(() => {
    let filtered = inventory?.filter(item =>
      item?.make?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.model?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.serialNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];
        
        if (typeof aValue === 'string') {
          aValue = aValue?.toLowerCase();
          bValue = bValue?.toLowerCase();
        }
        
        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [inventory, searchTerm, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedInventory?.length / itemsPerPage);
  const paginatedInventory = filteredAndSortedInventory?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(paginatedInventory?.map(item => item?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (itemId, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, itemId]);
    } else {
      onSelectionChange(selectedItems?.filter(id => id !== itemId));
    }
  };

  const isAllSelected = paginatedInventory?.length > 0 && 
    paginatedInventory?.every(item => selectedItems?.includes(item?.id));

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getAlertStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'triggered': return 'text-warning';
      case 'inactive': return 'text-text-secondary';
      default: return 'text-text-secondary';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card">
      {/* Search and Actions Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search by make, model, or serial number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            {selectedItems?.length > 0 && (
              <>
                <span className="text-sm text-text-secondary">
                  {selectedItems?.length} selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRevaluate(selectedItems)}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Revaluate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onConfigureAlert(selectedItems)}
                  iconName="Bell"
                  iconPosition="left"
                >
                  Set Alerts
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="w-12 px-6 py-4">
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              {[
                { key: 'make', label: 'Make/Model' },
                { key: 'year', label: 'Year' },
                { key: 'hours', label: 'Hours' },
                { key: 'serialNumber', label: 'Serial #' },
                { key: 'bookValue', label: 'Book Value' },
                { key: 'marketValue', label: 'Market Value' },
                { key: 'lastValuation', label: 'Last Valuation' },
                { key: 'alertStatus', label: 'Alert Status' }
              ]?.map(column => (
                <th
                  key={column?.key}
                  className="px-6 py-4 text-left text-sm font-medium text-text-primary cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort(column?.key)}
                >
                  <div className="flex items-center space-x-2">
                    <span>{column?.label}</span>
                    <Icon name={getSortIcon(column?.key)} size={14} />
                  </div>
                </th>
              ))}
              <th className="px-6 py-4 text-right text-sm font-medium text-text-primary">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedInventory?.map((item) => (
              <tr key={item?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4">
                  <Checkbox
                    checked={selectedItems?.includes(item?.id)}
                    onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-6 py-4">
                  <div>
                    <div className="font-medium text-text-primary">
                      {item?.make} {item?.model}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {item?.capacity}T • {item?.boomLength}ft
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-text-primary">{item?.year}</td>
                <td className="px-6 py-4 text-text-primary">
                  {item?.hours?.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-text-primary font-mono text-sm">
                  {item?.serialNumber}
                </td>
                <td className="px-6 py-4 text-text-primary font-medium">
                  {formatCurrency(item?.bookValue)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-text-primary">
                      {formatCurrency(item?.marketValue)}
                    </span>
                    {item?.marketValue < item?.bookValue && (
                      <Icon name="TrendingDown" size={16} className="text-warning" />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-text-secondary text-sm">
                  {formatDate(item?.lastValuation)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name="Bell" 
                      size={16} 
                      className={getAlertStatusColor(item?.alertStatus)} 
                    />
                    <span className={`text-sm capitalize ${getAlertStatusColor(item?.alertStatus)}`}>
                      {item?.alertStatus}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewDetails(item?.id)}
                    iconName="Eye"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {paginatedInventory?.map((item) => (
          <div key={item?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedItems?.includes(item?.id)}
                  onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
                />
                <div>
                  <h3 className="font-medium text-text-primary">
                    {item?.make} {item?.model}
                  </h3>
                  <p className="text-sm text-text-secondary">
                    {item?.year} • {item?.hours?.toLocaleString()} hrs
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewDetails(item?.id)}
                iconName="Eye"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-text-secondary">Book Value:</span>
                <div className="font-medium text-text-primary">
                  {formatCurrency(item?.bookValue)}
                </div>
              </div>
              <div>
                <span className="text-text-secondary">Market Value:</span>
                <div className="font-medium text-text-primary">
                  {formatCurrency(item?.marketValue)}
                </div>
              </div>
              <div>
                <span className="text-text-secondary">Last Valuation:</span>
                <div className="text-text-primary">
                  {formatDate(item?.lastValuation)}
                </div>
              </div>
              <div>
                <span className="text-text-secondary">Alert Status:</span>
                <div className={`capitalize ${getAlertStatusColor(item?.alertStatus)}`}>
                  {item?.alertStatus}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-secondary">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedInventory?.length)} of {filteredAndSortedInventory?.length} items
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
              />
              <span className="text-sm text-text-primary">
                {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTable;